import { useCallback, useEffect, useRef, useState } from "react";
import { X, LocateFixed, Loader2, TriangleAlert, Gauge, Volume2, VolumeX, Car } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { progress, nextStep, maneuverText, maneuverIcon, fmtDist } from "@/lib/nav";
import { riskLevel, roadReasons } from "@/lib/params";
import { routeIncidents, delayAhead } from "@/lib/traffic";
import { speak, spokenDist, voiceSupported } from "@/lib/voice";
import { REPORT_TYPES } from "@/lib/reportTypes";

const OFF_ROUTE_M = 150;

const STEP_MARKS = [1000, 300, 60];

// Voice prompts for upcoming maneuver, weather hazard and community incidents; each spoken once
function announce(s, nav, samples, incidents, pr, t, lang) {
  const step = nextStep(nav, pr.doneKm);
  const dM = (step.km - pr.doneKm) * 1000;
  const text = maneuverText(step, t).replace(" · ", ", ");
  const th = [...STEP_MARKS].reverse().find((x) => dM <= x);
  if (th && !s.said.has(`${step.km}:${th}`)) {
    STEP_MARKS.filter((x) => x >= th).forEach((x) => s.said.add(`${step.km}:${x}`));
    if (!(th === 1000 && dM < 400)) speak(th === 60 ? `${t("Şimdi")}, ${text}` : t("{d} sonra, {m}", { d: spokenDist(dM, lang), m: text }), lang);
  }
  const hz = samples.find((x) => x.km > pr.doneKm && x.km - pr.doneKm < 5 && (x.road || 0) >= 60);
  if (hz && !s.said.has(`hz:${hz.km}`)) {
    s.said.add(`hz:${hz.km}`);
    speak(t("Dikkat, {d} ileride {r}", { d: spokenDist((hz.km - pr.doneKm) * 1000, lang), r: t(roadReasons(hz)[0] || "Yüksek") }), lang);
  }
  const inc = incidents.find((i) => i.km > pr.doneKm && i.km - pr.doneKm < 3);
  if (inc && !s.said.has(`inc:${inc.id}`)) {
    s.said.add(`inc:${inc.id}`);
    speak(t("{d} ileride {r} bildirildi", { d: spokenDist((inc.km - pr.doneKm) * 1000, lang), r: t(REPORT_TYPES[inc.type].label) }), lang);
  }
}

export const useNavigation = (route, setRoute, reports) => {
  const { t, lang } = useI18n();
  const [muted, setMuted] = useState(() => localStorage.getItem("hd_voice_muted") === "1");
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const reportsRef = useRef(reports);
  reportsRef.current = reports;
  const say = useCallback((txt) => { if (!mutedRef.current) speak(txt, lang); }, [lang]);
  const toggleMute = () => setMuted((m) => { localStorage.setItem("hd_voice_muted", m ? "0" : "1"); if (!m) window.speechSynthesis?.cancel(); return !m; });
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState(null);
  const [prog, setProg] = useState(null);
  const [rerouting, setRerouting] = useState(false);
  const [follow, setFollow] = useState(true);
  const st = useRef({ watch: null, lock: null, lastIdx: 0, off: 0, startAt: 0, startS: null, busy: false, said: new Set() });
  const routeRef = useRef(route);
  routeRef.current = route;

  const stop = useCallback(() => {
    const s = st.current;
    if (s.watch != null) navigator.geolocation.clearWatch(s.watch);
    s.lock?.release?.().catch(() => {});
    s.watch = null; s.lock = null;
    setActive(false); setPos(null); setProg(null);
  }, []);

  const reroute = useCallback(async (p) => {
    const s = st.current, r = routeRef.current;
    if (s.busy || Date.now() < (s.nextReroute || 0)) return;
    s.busy = true; s.nextReroute = Date.now() + 20000; setRerouting(true);
    try {
      const { data } = await http.get("/route", { params: { from_lat: p.lat, from_lon: p.lon, to_lat: r.to.lat, to_lon: r.to.lon, depart: 0 } });
      setRoute((x) => ({ ...x, data, depart: 0 }));
      s.lastIdx = 0; s.off = 0; s.startAt = Date.now(); s.startS = 0;
      s.said = new Set();
      toast(t("Rota yeniden hesaplandı"));
      say(t("Rota yeniden hesaplandı"));
    } catch { /* keep old route */ }
    s.busy = false; setRerouting(false);
  }, [setRoute, t, say]);

  const onFix = useCallback((g) => {
    const s = st.current, data = routeRef.current.data, nav = data?.nav;
    const p = { lat: g.coords.latitude, lon: g.coords.longitude, heading: g.coords.heading, speed: g.coords.speed, acc: g.coords.accuracy };
    setPos(p);
    if (!nav) return;
    const pr = progress(nav, p, s.lastIdx);
    s.lastIdx = pr.idx;
    if (s.startS == null) { s.startS = pr.doneS; s.startAt = Date.now(); }
    const expected = pr.doneS - s.startS, actual = (Date.now() - s.startAt) / 1000;
    const pace = expected > 120 ? Math.min(2, Math.max(0.6, actual / expected)) : 1;
    const incidents = routeIncidents(nav, reportsRef.current);
    const trafficS = delayAhead(incidents, pr.doneKm, pr.remS);
    setProg({ ...pr, pace, trafficS, etaS: pr.remS * pace + trafficS });
    if (!mutedRef.current) announce(s, nav, data.samples, incidents, pr, t, lang);
    s.off = pr.offM > OFF_ROUTE_M ? s.off + 1 : 0;
    if (s.off >= 3) reroute(p);
    if (pr.remKm < 0.05) { toast.success(t("Hedefe vardınız")); say(t("Hedefe vardınız")); stop(); }
  }, [reroute, stop, t, lang, say]);

  const start = useCallback(async () => {
    if (!navigator.geolocation) return toast.error(t("Tarayıcınız konum desteklemiyor"));
    const s = st.current;
    Object.assign(s, { lastIdx: 0, off: 0, startS: null, said: new Set() });
    say(t("Navigasyon başladı"));
    s.watch = navigator.geolocation.watchPosition(onFix, (e) => { if (e.code === 1) { toast.error(t("Konum izni alınamadı")); stop(); } },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 20000 });
    try { s.lock = await navigator.wakeLock?.request("screen"); } catch { /* optional */ }
    setFollow(true); setActive(true);
  }, [onFix, stop, t, say]);

  useEffect(() => stop, [stop]);
  return { active, pos, prog, rerouting, follow, setFollow, start, stop, muted, toggleMute };
};

const clock = (secFromNow, lang) =>
  new Date(Date.now() + secFromNow * 1000).toLocaleTimeString(lang === "en" ? "en-GB" : "tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" });
const dur = (s, t) => { const m = Math.round(s / 60); return m < 60 ? `${m} ${t("dk")}` : `${Math.floor(m / 60)} ${t("sa")} ${m % 60} ${t("dk")}`; };

const Hazard = ({ samples, doneKm }) => {
  const { t } = useI18n();
  const h = samples.find((s) => s.km > doneKm && (s.road || 0) >= 30);
  if (!h) return null;
  const lvl = riskLevel(h.road), why = roadReasons(h)[0];
  return (
    <div data-testid="nav-hazard" className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm" style={{ background: `${lvl.color}1f`, color: lvl.color }}>
      <TriangleAlert size={16} />
      <span className="font-semibold">{fmtDist(h.km - doneKm)} {t("sonra")}: {t(why || lvl.label)}</span>
    </div>
  );
};

export const NavHud = ({ nav, route }) => {
  const { t, lang } = useI18n();
  const { prog, pos, rerouting, follow, setFollow, stop, muted, toggleMute } = nav;
  const data = route.data;
  const step = prog && nextStep(data.nav, prog.doneKm);
  const Icon = step ? maneuverIcon(step) : null;
  return (
    <>
      <div data-testid="nav-maneuver" className="keep-white absolute left-3 right-3 top-20 z-[1100] flex items-center gap-4 rounded-2xl bg-emerald-600 px-4 py-3 text-white shadow-2xl sm:left-1/2 sm:right-auto sm:w-[460px] sm:-translate-x-1/2">
        {!prog ? (
          <div className="flex items-center gap-3 font-semibold"><Loader2 className="animate-spin" /> {t("Konum bekleniyor…")}</div>
        ) : (
          <>
            <Icon size={40} strokeWidth={2.6} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <div data-testid="nav-step-distance" className="font-heading text-3xl font-black leading-none">{fmtDist(Math.max(0, step.km - prog.doneKm))}</div>
              <div data-testid="nav-step-text" className="mt-1 truncate text-sm font-semibold opacity-95">{maneuverText(step, t)}</div>
            </div>
          </>
        )}
        {rerouting && <span data-testid="nav-rerouting" className="flex items-center gap-1 text-xs"><Loader2 size={12} className="animate-spin" />{t("Yeniden hesaplanıyor")}</span>}
      </div>
      <div data-testid="nav-panel" className="glass absolute bottom-3 left-3 right-3 z-[1100] space-y-3 rounded-2xl p-4 sm:left-1/2 sm:right-auto sm:w-[460px] sm:-translate-x-1/2">
        {prog && <Hazard samples={data.samples} doneKm={prog.doneKm} />}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div data-testid="nav-eta" className="font-heading text-4xl font-black leading-none text-emerald-400">{prog ? clock(prog.etaS, lang) : "--:--"}</div>
            <div className="mt-1 font-mono text-xs text-slate-400">
              <span data-testid="nav-remaining-time">{prog ? dur(prog.etaS, t) : "—"}</span> · <span data-testid="nav-remaining-distance">{prog ? fmtDist(prog.remKm) : "—"}</span>
              {prog && prog.pace !== 1 && <span className="ml-1">({prog.pace > 1 ? "+" : ""}{Math.round((prog.pace - 1) * 100)}% {t("tempo")})</span>}
            </div>
            {prog?.trafficS > 0 && (
              <div data-testid="nav-traffic-delay" className="mt-1 flex items-center gap-1 text-xs font-semibold text-orange-400">
                <Car size={12} /> {t("Trafik")} +{dur(prog.trafficS, t)}
              </div>
            )}
          </div>
          <div className="flex flex-col items-center rounded-xl bg-white/[0.04] px-3 py-1.5">
            <Gauge size={14} className="text-sky-400" />
            <span data-testid="nav-speed" className="font-heading text-2xl font-bold text-white">{pos?.speed != null ? Math.round(pos.speed * 3.6) : "—"}</span>
            <span className="font-mono text-[9px] text-slate-500">{t("km/sa")}</span>
          </div>
          {!follow && (
            <button data-testid="nav-recenter-btn" onClick={() => setFollow(true)} className="rounded-full bg-sky-400 p-3 text-slate-950 hover:bg-sky-300"><LocateFixed size={20} /></button>
          )}
          {voiceSupported() && (
            <button data-testid="nav-voice-toggle" onClick={toggleMute} title={t(muted ? "Sesi aç" : "Sesi kapat")}
              className={`rounded-full p-3 ${muted ? "bg-white/5 text-slate-400" : "bg-sky-500/20 text-sky-300"}`}>
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          )}
          <button data-testid="nav-stop-btn" onClick={stop} className="keep-white rounded-full bg-red-600 p-3 text-white hover:bg-red-500" title={t("Navigasyonu bitir")}><X size={20} /></button>
        </div>
      </div>
    </>
  );
};
